import React from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fetchSmsPreferences, updateSmsPreferences, disconnectGoogle } from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { LanguagePreferenceCard } from "@/components/LanguagePreferenceCard";
import { useLocalization } from "@voltie/localization-web";
import { localization, useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

/** Visible legal link — a real link when configured, else a clearly-marked
 *  placeholder (never an invented legal URL). */
function LegalLink({ label, url }: { label: string; url: string }) {
  const t = useT();
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="underline hover:opacity-80">
        {label}
      </a>
    );
  }
  return (
    <span className="underline decoration-dotted opacity-70" title={t("operator.account.legal_url_not_configured")}>
      {t("operator.auth.legal_link_not_configured", { label })}
    </span>
  );
}

/** Connect / disconnect "Sign in with Google" for the current account. Shown
 *  only when Google login is configured on the backend. */
function ConnectedAccountsCard() {
  const t = useT();
  const { googleLoginEnabled, googleLinked, refetch } = useAuth();

  // Surface the outcome of a Connect round trip (the callback redirects back to
  // /account?google=...), then strip the param so a refresh doesn't re-toast.
  React.useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("google");
    if (!code) return;
    if (code === "connected") {
      toast.success(t("operator.account.google_connected_toast"));
      refetch();
    } else if (code === "already_linked") {
      toast.error(t("operator.account.google_already_linked"));
    } else if (code === "disabled" || code === "google_disabled") {
      toast.error(t("operator.account.google_disabled"));
    } else {
      toast.error(t("operator.account.google_connect_failed"));
    }
    window.history.replaceState({}, "", "/account");
  }, [refetch, t]);

  const disconnectM = useMutation({
    mutationFn: disconnectGoogle,
    onSuccess: () => {
      toast.success(t("operator.account.google_disconnected_toast"));
      refetch();
    },
    onError: (e) =>
      toast.error(errorMessage(t, e, { family: "account_google_disconnect", fallback: "operator.account.google_disconnect_failed" })),
  });

  if (!googleLoginEnabled) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("operator.account.connected_title")}</CardTitle>
        <CardDescription>
          {t("operator.account.connected_description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">{t("operator.account.google")}</span>
            {googleLinked ? (
              <Badge variant="secondary">{t("operator.account.google_linked")}</Badge>
            ) : (
              <Badge variant="outline">{t("operator.account.google_not_linked")}</Badge>
            )}
          </div>
          {googleLinked ? (
            <Button
              variant="outline"
              onClick={() => disconnectM.mutate()}
              disabled={disconnectM.isPending}
            >
              {disconnectM.isPending ? t("operator.account.google_disconnecting") : t("operator.account.google_disconnect")}
            </Button>
          ) : (
            <Button onClick={() => { window.location.href = "/auth/google/connect"; }}>
              {t("operator.account.google_connect")}
            </Button>
          )}
        </div>
        <p className="mt-2 text-xs opacity-60">
          {t("operator.account.google_help")}
        </p>
      </CardContent>
    </Card>
  );
}

export function AccountSettingsPage() {
  const t = useT();
  const qc = useQueryClient();
  // The legal disclosure follows the language shown in this tab (product or
  // preview); a switch only refetches it, it saves nothing.
  const { language } = useLocalization(localization);
  const prefsQ = useQuery({
    queryKey: ["sms-preferences", language],
    queryFn: () => fetchSmsPreferences(language),
    placeholderData: keepPreviousData,
  });
  const p = prefsQ.data;

  const [checked, setChecked] = React.useState(false);
  React.useEffect(() => {
    if (p) setChecked(p.consent_active);
  }, [p?.consent_active]);

  const saveM = useMutation({
    // An opt-in names the evidence version of the disclosure on screen.
    mutationFn: ({ optIn, version }: { optIn: boolean; version: string }) => updateSmsPreferences(optIn, version),
    onSuccess: () => {
      toast.success(t("operator.account.sms_saved"));
      qc.invalidateQueries({ queryKey: ["sms-preferences"] });
      qc.invalidateQueries({ queryKey: ["reset-availability"] });
    },
    onError: (e) =>
      toast.error(errorMessage(t, e, { family: "account_sms_preferences", fallback: "operator.account.sms_save_failed" })),
  });

  const canCheck = Boolean(p?.opt_in_eligible);
  // Backend kill switch: when the SMS channel is disabled the endpoint returns
  // only { feature_enabled: false } and the opt-in section is hidden entirely.
  const smsDisabled = p != null && p.feature_enabled === false;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-4 pt-[86px]">
      <h1 className="text-xl font-semibold">{t("operator.account.title")}</h1>

      <LanguagePreferenceCard />

      <Card>
        <CardHeader>
          <CardTitle>{t("operator.account.sms_title")}</CardTitle>
          <CardDescription>
            {t("operator.account.sms_description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {prefsQ.isLoading && <div className="text-sm opacity-70">{t("common.state.loading")}</div>}
          {prefsQ.isError && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {t("operator.account.sms_load_failed")}
            </div>
          )}

          {smsDisabled && (
            <div className="text-sm opacity-70">
              {t("operator.account.sms_unavailable")}
            </div>
          )}

          {p && !smsDisabled && (
            <div className="space-y-4">
              {/* Phone number + verification */}
              <div>
                <div className="text-xs uppercase opacity-60">{t("operator.account.phone_number")}</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-medium">{p.masked_phone || t("operator.account.no_phone")}</span>
                  {p.phone_verified ? (
                    <Badge variant="secondary">{t("operator.account.phone_verified")}</Badge>
                  ) : (
                    <Badge variant="outline">{t("operator.account.phone_not_verified")}</Badge>
                  )}
                </div>
                {!canCheck && (
                  <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    {t("operator.account.phone_required")}
                  </div>
                )}
              </div>

              {/* Opt-in checkbox + full disclosure (all visible for the screenshot) */}
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={checked}
                  disabled={!canCheck || saveM.isPending}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <span className="text-sm font-medium">
                  {t("operator.account.sms_opt_in")}
                </span>
              </label>

              {/* Server disclosure, rendered exactly as sent: its SHA-256 is the consent evidence (D-7). */}
              <p lang={p.disclosure_language} dir="auto" className="whitespace-pre-line text-xs leading-relaxed opacity-80">
                {p.disclosure_text}
              </p>

              <div className="text-xs">
                <LegalLink label={t("operator.auth.privacy_policy")} url={p.privacy_policy_url} />
                <span className="px-2 opacity-40">|</span>
                <LegalLink label={t("operator.auth.terms_of_service")} url={p.terms_of_service_url} />
              </div>

              <Button
                onClick={() => saveM.mutate({ optIn: checked, version: p.consent_text_version })}
                // While another language's disclosure loads, the one on screen is about to change.
                disabled={saveM.isPending || prefsQ.isPlaceholderData || (!canCheck && !p.consent_active)}
              >
                {saveM.isPending ? t("common.state.saving") : t("operator.account.sms_save")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ConnectedAccountsCard />
    </div>
  );
}
